import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router";
import CasinoHeading from "../../casinoComponents/casinoHeading";
import VideoBox from "../../casinoComponents/videoBox";
import CasinoLastResult from "../../casinoComponents/casinoLastResult";
import MyBet from "../../casinoComponents/myBet";
import CasinoTableLayout from "../../casinoComponents/casinoTableLayout";
import { getCasinoGameDetails, getMatchExposure, getCasinoOpenBets } from "../../../apiservices/CasionApi";
import ViewMoreModal from "../../../components/ViewMoreModal";
import { extractCasinoGame } from "../../../utils/casinoGame";
import { formatBetNation } from "../../../utils/casinoMyBets";
import { IS_REF_MOBILE } from "../../../utils/refViewport";
import RRefResults from "../andarBahar34/RRefResults";
import AbjRefResults from "./AbjRefResults";
import { Ab20VideoCards, AbjVideoCards } from "./AbVideoSliders";
import { Ab20BetTable, AbjBetTable } from "./AbBetTables";
import Ab20ResultBody from "../../casinoComponents/refResultModal/Ab20ResultBody";
import AbjResultBody from "../../casinoComponents/refResultModal/AbjResultBody";
import "../../../styles/games/andarbahar.css";

// Reference default titles (then lastresults2 res1.cname, which is the same)
const AB_TITLES = {
  ab20: "Andar Bahar",
  abj: "Andar Bahar 2",
};

export default function AndarBahar() {
  const [gameData, setGameData] = useState(null);
  const [exposures, setExposures] = useState({});
  const [myBets, setMyBets] = useState([]);
  const [showViewMoreModal, setShowViewMoreModal] = useState(false);

  const { pathname } = useLocation();
  const gameName = extractCasinoGame(pathname);
  const iframeSrc = `https://stream-s-43.uhdmovies.online/casino-stream?id=${gameName}`;

  useEffect(() => {
    const gameName = extractCasinoGame(pathname);
    const fetchGameData = async () => {
      try {
        const response = await getCasinoGameDetails(gameName);
        setGameData(response);
      } catch (err) {
        console.error("API Fetch Error:", err.message);
        setGameData(null); // clear data on error
      }
    };

    fetchGameData();
    const interval = setInterval(fetchGameData, 1500);
    return () => clearInterval(interval);
  }, [pathname]);

  const handleBetClick = (value) => {
    if (!value) return;
    fetchExposure();
    fetchMyBetsData();
  };

  const matchId = gameData?.data?.data?.mid?.toString();

  const fetchExposure = useCallback(async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user?.user_id || user?.id || user?.staff_id || user?.owner_id;
      if (userId && matchId) {
        const response = await getMatchExposure(userId, matchId);
        if (response.success && response.data) {
          const exposureMap = {};
          response.data.forEach((item) => {
            const teamName = item.team_name || item.selection || item.player_name || "";
            const exposureAmount = parseFloat(item.exposure_amount) || 0;
            if (teamName && exposureAmount !== 0) {
              exposureMap[teamName] = exposureAmount;
              exposureMap[teamName.toLowerCase()] = exposureAmount;
              exposureMap[teamName.trim()] = exposureAmount;
            }
          });
          setExposures(exposureMap);
        }
      }
    } catch (error) {
      console.error('Error fetching exposure:', error);
    }
  }, [matchId]);

  // My Bets: same API as lord-admin – GET /lords/bet-list/casino/open-downline?gameName=...
  const fetchMyBetsData = useCallback(async () => {
    const gameName = extractCasinoGame(pathname);
    if (!gameName) return;
    try {
      const response = await getCasinoOpenBets(gameName);
      if (response?.success && Array.isArray(response.data)) {
        const formattedBets = response.data.map((bet) => ({
          // My Bets "Gametype" column (casino_bets.game_name, e.g. mogambo)
          description: bet.description ?? bet.game_name ?? bet.gameName,
          userName: bet.username || bet.userName || "-",
          matchedBet: bet.matchedBet || bet.nation || bet.selection || bet.player_name || "-",
          selection: bet.selection || bet.player_name || bet.matchedBet || "",
          player_name: bet.player_name || "",
          odds: bet.odds ?? bet.userrate ?? "0",
          stake: bet.stake ?? bet.amount ?? "0",
          placeDate: bet.placeDate ?? bet.created_at ?? "-",
          gametype: bet.gametype ?? bet.game_type ?? (bet.type === "lay" ? "Lay" : bet.type === "back" ? "Back" : "-"),
          type: bet.type || null,
          exposer: parseFloat(bet.exposer || bet.exposure_amount || "0") || 0
        }));
        setMyBets(formattedBets);
      }
    } catch (error) {
      console.error("Error fetching my bets (open-downline):", error);
    }
  }, [pathname]);

  // Fetch exposure and My Bets on mount and periodically (My Bets every 5s like lord-admin)
  useEffect(() => {
    if (matchId) {
      const t = setTimeout(() => {
        fetchExposure();
      }, 2000);
      const exposureInterval = setInterval(fetchExposure, 2000);
      return () => {
        clearTimeout(t);
        clearInterval(exposureInterval);
      };
    }
  }, [fetchExposure, matchId]);

  useEffect(() => {
    fetchMyBetsData();
    const intervalId = setInterval(fetchMyBetsData, 3000);
    return () => clearInterval(intervalId);
  }, [fetchMyBetsData]);


  const feed = gameData?.data?.data ?? {};
  const mid = feed?.mid || gameData?.data?.mid || "";
  const title = AB_TITLES[gameName] || "";
  const isAbj = gameName === "abj";
  // Andar Bahar 2: stored "1st Bet SA" reads "1st Bet A"
  const formatNation = (nat, bet) => formatBetNation(gameName, nat, bet);

  // Reference pages: ab20 = .casino-table.andar-bahar, abj = .casino-table.andar-bahar2 —
  // .game-header, video (+ card sliders), then (once the feed has a round) .casino-detail with the bet
  // area, remark, Last Result.
  return (
    <CasinoTableLayout
      tableClass={isAbj ? "andar-bahar2" : "andar-bahar"}
      rightSide={<MyBet bets={myBets} onViewMore={() => setShowViewMoreModal(true)} formatNation={formatNation} />}
    >
      <>
        <CasinoHeading name={title} roundId={feed?.mid} />{" "}
        <VideoBox
          iframeSrc={iframeSrc}
          timerValue={feed?.lt}
          gameType={gameName}
          cardsOverlay={
            isAbj ? (
              feed?.card ? <AbjVideoCards cardString={feed.card} variant="video" /> : null
            ) : (
              <Ab20VideoCards feed={feed} />
            )
          }
        />{" "}
        {mid ? (
          <div className="casino-detail">
            {isAbj && IS_REF_MOBILE ? <AbjVideoCards cardString={feed?.card} variant="detail" /> : null}
            {isAbj ? " " : null}
            {isAbj ? (
              <AbjBetTable gameType={gameName} feed={feed} exposures={exposures} onBetClick={handleBetClick} />
            ) : (
              <Ab20BetTable gameType={gameName} feed={feed} exposures={exposures} onBetClick={handleBetClick} />
            )}{" "}
            {feed?.remark ? (
              <div className={`casino-remark ${isAbj ? "mt-3" : "mt-1"}`}>
                <div className="remark-icon">
                  <img src={`${import.meta.env.BASE_URL}assets/img/icons/remark.png`} alt="" />
                </div>{" "}
                <marquee>{feed.remark}</marquee>
              </div>
            ) : null}{" "}
            <CasinoLastResult
              gameType={gameName}
              gameData={gameData}
              resultComponent={
                isAbj ? (
                  <AbjRefResults
                    gameType={gameName}
                    mid={mid}
                    fallbackTitle={title}
                    renderBody={(t1) => <AbjResultBody t1={t1} />}
                  />
                ) : (
                  <RRefResults
                    gameType={gameName}
                    mid={mid}
                    fallbackTitle={title}
                    renderBody={(t1) => <Ab20ResultBody t1={t1} />}
                  />
                )
              }
            />
          </div>
        ) : null}
      </>
      <ViewMoreModal
        show={showViewMoreModal}
        onHide={() => setShowViewMoreModal(false)}
        title="View More"
        sectionKey={gameName}
        formatNation={formatNation}
      />
    </CasinoTableLayout>
  );
}
